import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { OAuth2Agent, type UserInfo } from 'acesso-cidadao-oauth2-lib'
import LoadingAuth from 'components/LoadingAuth'
import { isEmptyObject } from 'utils/utils'
interface AuthContextProps {
  authData: AuthDataTypes
  handleLogin: () => Promise<void>
  handleLogout: () => void
}

interface AuthContextProviderProps {
  children: React.ReactNode
}

export interface AuthDataTypes {
  token: string | undefined
  user: Omit<UserInfo, 'userAccountInfo'>
  roles: string[]
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps)

export const oauth2Agent = new OAuth2Agent({
  appName: process.env.REACT_APP_DEMO_APP_NAME,
  appVersion: process.env.REACT_APP_DEMO_APP_VERSION,
  appKey: process.env.REACT_APP_DEMO_APP_KEY,
  env: process.env.NODE_ENV, // Optional. Predefines the server URL. Default: 'production'.
  loginWebApp: process.env.REACT_APP_DEMO_LOGIN_WEB_APP, // Optional. Overrides env's login webapp URL.
  loginServer: process.env.REACT_APP_DEMO_LOGIN_SERVER, // Optional. Overrides env's server URL.
  redirectEnabled: true,
})

export function AuthContextProvider({ children }: AuthContextProviderProps) {
  const [loading, setLoading] = useState<boolean>(false)

  const [authData, setAuthData] = useState<AuthDataTypes>({} as AuthDataTypes)

  const handleLogin = async () => {
    setLoading(true)

    const token = await oauth2Agent.boot()

    oauth2Agent.me().then(
      (response) => {
        setAuthData({
          token,
          roles: response.roles,
          user: response.client,
        })
        setLoading(false)
      },
      () => {
        oauth2Agent.login()
      }
    )
  }

  const handleLogout = () => {
    oauth2Agent.logout()
  }

  useEffect(() => {
    void handleLogin()
  }, [])

  const valuesProvider = useMemo(() => {
    return {
      authData,
      handleLogin,
      handleLogout,
    }
  }, [authData, handleLogin, handleLogout])

  return (
    <AuthContext.Provider value={valuesProvider}>
      {authData.token !== undefined && authData.token !== '' && children}
      {loading && <LoadingAuth />}
    </AuthContext.Provider>
  )
}

export function useAuthentication() {
  const context = useContext(AuthContext)

  if (isEmptyObject(context)) {
    throw new Error(
      'useAuthentication must be used within a AuthContextProvider'
    )
  }

  return context
}
